// Apply-selected-address scope card.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { SavedAddress, SubscriptionTarget } from '../mockData.js';
import { SCOPE_LABELS } from '../mockData.js';
import { WriteButton } from '../../_w2a_lower/liveWrite.js';

export type ScopeKey = 'order' | 'default' | 'subscription';

export interface AddressScopeCardProps {
  /** Null when the customer has no saved address yet. */
  selected: SavedAddress | null;
  scope: ScopeKey;
  onScopeChange: (next: ScopeKey) => void;
  subscriptions: SubscriptionTarget[];
  subscriptionId: string;
  onSubscriptionChange: (id: string) => void;
  onReview: () => void;
  /** Copy per scope; defaults to the design fixture copy. */
  scopeCopy?: typeof SCOPE_LABELS;
  /** Caption under "Current draft order only". */
  draftOrderLabel?: string;
  /** W31: true on a real session; Review stays visible but inert. */
  liveWritesPaused?: boolean;
}

export function AddressScopeCard({
  selected,
  scope,
  onScopeChange,
  subscriptions,
  subscriptionId,
  onSubscriptionChange,
  onReview,
  scopeCopy = SCOPE_LABELS,
  draftOrderLabel = 'Draft order #D-1048',
  liveWritesPaused = false,
}: AddressScopeCardProps): ReactNode {
  const copy = scopeCopy[scope];
  return (
    <section className="card ca-card ca-scope-card">
      <div className="ca-card-head">
        <div>
          <Icon id="arrow" />
          <h2>Apply selected address</h2>
        </div>
      </div>
      <form
        className="ca-body"
        id="ca-scope-form"
        onSubmit={event => {
          event.preventDefault();
          if (liveWritesPaused) return;
          onReview();
        }}
      >
        <div className="ca-selected-address">
          <span className="eyebrow">SELECTED ADDRESS</span>
          {selected ? (
            <>
              <strong id="ca-selected-label">{selected.label}</strong>
              <p id="ca-selected-address">
                {selected.recipient}, {selected.line1}, {selected.city}, {selected.state}{' '}
                {selected.zip}
              </p>
            </>
          ) : (
            <p id="ca-selected-address" className="muted">
              No address selected
            </p>
          )}
        </div>
        <fieldset className="ca-scope-options">
          <legend>Use this address for</legend>
          <label>
            <input
              type="radio"
              name="scope"
              value="order"
              checked={scope === 'order'}
              onChange={() => onScopeChange('order')}
            />
            <span>
              <b>Current draft order only</b>
              <small>{draftOrderLabel}</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="scope"
              value="default"
              checked={scope === 'default'}
              onChange={() => onScopeChange('default')}
            />
            <span>
              <b>Default customer address</b>
              <small>For future orders</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="scope"
              value="subscription"
              checked={scope === 'subscription'}
              onChange={() => onScopeChange('subscription')}
            />
            <span>
              <b>Selected subscription</b>
              <small>Choose one schedule</small>
            </span>
          </label>
        </fieldset>
        <label
          className="ca-subscription-select"
          id="ca-subscription-scope"
          hidden={scope !== 'subscription'}
        >
          Subscription
          <select
            id="ca-subscription"
            value={subscriptionId}
            onChange={event => onSubscriptionChange(event.target.value)}
            disabled={subscriptions.length === 0}
          >
            {subscriptions.length === 0 ? (
              <option value="">No subscription schedule linked yet</option>
            ) : null}
            {subscriptions.map(sub => (
              <option key={sub.id} value={sub.id}>
                {sub.label}
              </option>
            ))}
          </select>
        </label>
        <div className="ca-scope-description">
          <Icon id="target" />
          <div>
            <strong id="ca-scope-title">{copy.heading}</strong>
            <p id="ca-scope-description">{copy.detail}</p>
            <small id="ca-current-destination">
              {selected
                ? `Current destination: ${selected.city}, ${selected.state}`
                : 'Current destination: none on file'}
            </small>
          </div>
        </div>
        <WriteButton
          paused={liveWritesPaused}
          type="submit"
          className="ca-blue ca-wide"
          actionLabel="Review address change"
        >
          Review address change
          <Icon id="arrow" />
        </WriteButton>
        <p className="ca-scope-note">
          Review the address check and the exact scope before confirming.
        </p>
      </form>
    </section>
  );
}
